import React, { useMemo } from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import PieChartOutlineOutlinedIcon from "@mui/icons-material/PieChartOutlineOutlined";

export type TreatmentTrendPoint = {
  label: string;
  procedures: number;
  value: number;
};

export type TreatmentBreakdownItem = {
  label: string;
  value: number;
};

const CHART_SX = {
  "& .MuiChartsAxis-tickLabel": {
    fill: "#64748b",
    fontSize: "11px",
  },
};

const PIE_COLORS = [
  "#0d9488",
  "#2563eb",
  "#7c3aed",
  "#db2777",
  "#ea580c",
  "#ca8a04",
  "#16a34a",
  "#0891b2",
];

function formatTrendLabel(label: string) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(label)) {
    const date = new Date(`${label}T12:00:00`);
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  const parts = label.split("-");
  if (parts.length === 2) {
    const month = Number(parts[1]);
    if (month >= 1 && month <= 12) {
      const date = new Date(Number(parts[0]), month - 1, 1);
      return date.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
    }
    return `Wk ${parts[1]}`;
  }
  return label;
}

function ChartPanel({
  icon: Icon,
  title,
  subtitle,
  children,
}: {
  icon: React.ElementType;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm shadow-slate-200/40 dark:border-slate-700/80 dark:bg-slate-800/60 dark:shadow-none sm:p-6">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
          <Icon sx={{ fontSize: 20 }} />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{title}</h3>
          {subtitle ? (
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>
          ) : null}
        </div>
      </div>
      {children}
    </div>
  );
}

type TreatmentReportChartsProps = {
  trend: TreatmentTrendPoint[];
  breakdown: TreatmentBreakdownItem[];
  loading: boolean;
};

export default function TreatmentReportCharts({
  trend,
  breakdown,
  loading,
}: TreatmentReportChartsProps) {
  const trendLabels = useMemo(
    () => trend.map((p) => formatTrendLabel(p.label)),
    [trend]
  );

  const pieData = useMemo(() => {
    const top = breakdown.slice(0, 8);
    const rest = breakdown.slice(8);
    const restValue = rest.reduce((s, item) => s + item.value, 0);
    const slices = top.map((item, index) => ({
      id: index,
      label: item.label,
      value: item.value,
      color: PIE_COLORS[index % PIE_COLORS.length],
    }));
    if (restValue > 0) {
      slices.push({
        id: top.length,
        label: "Other",
        value: restValue,
        color: "#94a3b8",
      });
    }
    return slices;
  }, [breakdown]);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <ChartPanel
        icon={BarChartOutlinedIcon}
        title="Procedures over time"
        subtitle="Volume in the selected period"
      >
        {loading ? (
          <div className="flex h-[260px] items-center justify-center text-sm text-slate-400">
            Loading…
          </div>
        ) : trend.length === 0 ? (
          <div className="flex h-[260px] items-center justify-center text-sm text-slate-400">
            No trend data for this period
          </div>
        ) : (
          <BarChart
            sx={CHART_SX}
            height={260}
            series={[
              {
                data: trend.map((p) => p.procedures),
                label: "Procedures",
                color: "#0d9488",
              },
            ]}
            xAxis={[{ scaleType: "band", data: trendLabels }]}
            margin={{ left: 48, right: 16, top: 16, bottom: 56 }}
          />
        )}
      </ChartPanel>

      <ChartPanel
        icon={PieChartOutlineOutlinedIcon}
        title="By procedure type"
        subtitle="Share of procedures in period"
      >
        {loading ? (
          <div className="flex h-[260px] items-center justify-center text-sm text-slate-400">
            Loading…
          </div>
        ) : pieData.length === 0 ? (
          <div className="flex h-[260px] items-center justify-center text-sm text-slate-400">
            No breakdown data
          </div>
        ) : (
          <div className="flex justify-center">
            <PieChart
              series={[
                {
                  data: pieData,
                  innerRadius: 50,
                  outerRadius: 100,
                  paddingAngle: 2,
                  cornerRadius: 4,
                },
              ]}
              width={320}
              height={260}
              slotProps={{ legend: { hidden: true } }}
            />
          </div>
        )}
      </ChartPanel>
    </div>
  );
}
